import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { motion } from 'framer-motion';

const ApplicationFormSection = ({ currentLanguage, isEligible }) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors }, watch, trigger } = useForm();

  const content = {
    EN: {
      title: "Complete Your Application",
      subtitle: "Just a few more details to get your PSPXpress card",
      sections: [
        {
          title: "Personal Information",
          icon: "User",
          fields: [
            { name: 'firstName', label: 'First Name', type: 'text', required: true },
            { name: 'lastName', label: 'Last Name', type: 'text', required: true },
            { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', required: true },
            { name: 'phoneNumber', label: 'Phone Number', type: 'tel', required: true }
          ]
        },
        {
          title: "Address Information",
          icon: "MapPin",
          fields: [
            { name: 'address', label: 'Street Address', type: 'text', required: true },
            { name: 'city', label: 'City', type: 'text', required: true },
            { name: 'province', label: 'Province', type: 'select', required: true, options: [
              { value: 'ON', label: 'Ontario' },
              { value: 'QC', label: 'Quebec' },
              { value: 'BC', label: 'British Columbia' },
              { value: 'AB', label: 'Alberta' },
              { value: 'MB', label: 'Manitoba' },
              { value: 'SK', label: 'Saskatchewan' },
              { value: 'NS', label: 'Nova Scotia' },
              { value: 'NB', label: 'New Brunswick' },
              { value: 'NL', label: 'Newfoundland and Labrador' },
              { value: 'PE', label: 'Prince Edward Island' },
              { value: 'NT', label: 'Northwest Territories' },
              { value: 'NU', label: 'Nunavut' },
              { value: 'YT', label: 'Yukon' }
            ]},
            { name: 'postalCode', label: 'Postal Code', type: 'text', required: true }
          ]
        },
        {
          title: "Account Setup",
          icon: "Settings",
          fields: [
            { name: 'email', label: 'Email Address', type: 'email', required: true },
            { name: 'password', label: 'Password', type: 'password', required: true },
            { name: 'confirmPassword', label: 'Confirm Password', type: 'password', required: true }
          ]
        }
      ],
      navigation: {
        next: "Next",
        previous: "Previous", 
        submit: "Submit Application"
      },
      success: {
        title: "Application Submitted Successfully!",
        message: "Thank you for applying for a PSPXpress card. You\'ll receive a confirmation email shortly with next steps.",
        cardDelivery: "Your card will be delivered within 7-10 business days.",
        trackApplication: "Track Your Application"
      }
    },
    FR: {
      title: "Complétez votre demande",
      subtitle: "Juste quelques détails de plus pour obtenir votre carte PSPXpress",
      sections: [
        {
          title: "Informations personnelles",
          icon: "User", 
          fields: [
            { name: 'firstName', label: 'Prénom', type: 'text', required: true },
            { name: 'lastName', label: 'Nom de famille', type: 'text', required: true },
            { name: 'dateOfBirth', label: 'Date de naissance', type: 'date', required: true },
            { name: 'phoneNumber', label: 'Numéro de téléphone', type: 'tel', required: true }
          ]
        },
        {
          title: "Informations d\'adresse",
          icon: "MapPin",
          fields: [
            { name: 'address', label: 'Adresse de rue', type: 'text', required: true },
            { name: 'city', label: 'Ville', type: 'text', required: true },
            { name: 'province', label: 'Province', type: 'select', required: true, options: [
              { value: 'ON', label: 'Ontario' },
              { value: 'QC', label: 'Québec' },
              { value: 'BC', label: 'Colombie-Britannique' },
              { value: 'AB', label: 'Alberta' },
              { value: 'MB', label: 'Manitoba' },
              { value: 'SK', label: 'Saskatchewan' },
              { value: 'NS', label: 'Nouvelle-Écosse' },
              { value: 'NB', label: 'Nouveau-Brunswick' },
              { value: 'NL', label: 'Terre-Neuve-et-Labrador' },
              { value: 'PE', label: 'Île-du-Prince-Édouard' },
              { value: 'NT', label: 'Territoires du Nord-Ouest' },
              { value: 'NU', label: 'Nunavut' },
              { value: 'YT', label: 'Yukon' }
            ]},
            { name: 'postalCode', label: 'Code postal', type: 'text', required: true }
          ]
        },
        {
          title: "Configuration du compte",
          icon: "Settings",
          fields: [
            { name: 'email', label: 'Adresse e-mail', type: 'email', required: true },
            { name: 'password', label: 'Mot de passe', type: 'password', required: true },
            { name: 'confirmPassword', label: 'Confirmer le mot de passe', type: 'password', required: true }
          ]
        }
      ],
      navigation: {
        next: "Suivant",
        previous: "Précédent",
        submit: "Soumettre la demande"
      },
      success: {
        title: "Demande soumise avec succès!",
        message: "Merci d\'avoir postulé pour une carte PSPXpress. Vous recevrez un e-mail de confirmation sous peu avec les prochaines étapes.",
        cardDelivery: "Votre carte sera livrée dans les 7-10 jours ouvrables.",
        trackApplication: "Suivre votre demande"
      }
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('Application submitted:', data);
    setIsSubmitting(false);
    setSubmitted(true);
  };

  const handleNext = async () => {
    const currentFields = text?.sections?.[currentSection]?.fields?.map(field => field?.name) || [];
    const isValid = await trigger(currentFields);
    
    if (isValid) {
      setCurrentSection(prev => Math.min(prev + 1, text?.sections?.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentSection(prev => Math.max(prev - 1, 0));
  };

  const renderField = (field) => {
    const commonProps = {
      id: field?.name,
      ...register(field?.name, { 
        required: field?.required ? `${field?.label} is required` : false,
        validate: field?.name === 'confirmPassword' ? (value) => {
          const password = watch('password');
          return value === password || 'Passwords do not match';
        } : undefined
      }),
      error: errors?.[field?.name]?.message,
      className: "mb-6"
    };

    if (field?.type === 'select') {
      return (
        <Select
          key={field?.name}
          label={field?.label}
          options={field?.options || []}
          placeholder={`Select ${field?.label}`}
          {...commonProps}
        />
      );
    }

    return (
      <Input
        key={field?.name}
        type={field?.type}
        label={field?.label}
        placeholder={field?.label}
        {...commonProps}
      />
    );
  };

  if (submitted) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center p-8 bg-white rounded-2xl shadow-card"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={40} className="text-green-600" />
              </div>

              <h2 className="text-2xl font-bold text-green-800 mb-4">
                {text?.success?.title}
              </h2>

              <p className="text-green-700 mb-6 leading-relaxed">
                {text?.success?.message}
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
                <p className="text-green-800 font-medium">
                  {text?.success?.cardDelivery}
                </p>
              </div>

              <Button
                variant="default"
                size="lg"
                className="bg-green-600 hover:bg-green-700 px-8 py-4 text-lg"
                iconName="Search"
                iconPosition="right"
              >
                {text?.success?.trackApplication}
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {text?.title}
            </h2>
            <p className="text-lg text-gray-600">
              {text?.subtitle}
            </p>
          </motion.div>

          {/* Section Tabs */}
          <div className="flex justify-center mb-8">
            <div className="flex space-x-1 bg-gray-200 p-1 rounded-lg">
              {text?.sections?.map((section, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSection(index)}
                  disabled={index > currentSection}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentSection === index
                      ? 'bg-white text-primary shadow-sm'
                      : currentSection > index
                      ? 'text-gray-600 hover:text-gray-800' :'text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Icon name={section?.icon} size={16} />
                  <span className="hidden sm:inline">{section?.title}</span>
                </button>
              ))}
            </div>
          </div>

          <motion.div
            key={currentSection}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-card p-8"
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name={text?.sections?.[currentSection]?.icon} size={20} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {text?.sections?.[currentSection]?.title}
                  </h3>
                </div>

                <div className="space-y-6">
                  {text?.sections?.[currentSection]?.fields?.map(renderField)}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentSection === 0}
                  iconName="ArrowLeft"
                  iconPosition="left"
                >
                  {text?.navigation?.previous}
                </Button>

                {currentSection === text?.sections?.length - 1 ? (
                  <Button
                    type="submit"
                    variant="default"
                    disabled={isSubmitting}
                    className="px-8"
                    iconName={isSubmitting ? "Loader2" : "Send"}
                    iconPosition="right"
                  >
                    {isSubmitting ? 'Submitting...' : text?.navigation?.submit}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleNext}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    {text?.navigation?.next}
                  </Button>
                )}
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ApplicationFormSection;